/**
 * @file use-stock-selection.ts
 * @description Выбор строк остатка для массового размещения
 * (phase19.24 этап 1, шаг 4).
 *
 * Галочки существуют только в отборе «Без места»: в общем списке остатков
 * массовое действие бессмысленно — размещать нечего, а колонка с чекбоксами
 * съедала бы ширину на 360px.
 *
 * Выбор хранится множеством id строк, а не копиями самих строк: список
 * перечитывается после каждой мутации (один источник правды — сервер), и
 * строка, которой в отборе больше нет, обязана выпасть из выбора сама.
 *
 * `?select=all` — вход с дашборда: плитка «Без места» и строка «Требует
 * внимания» ведут в остатки с уже отмеченными партиями, чтобы кладовщик сразу
 * жал «Разместить выбранные». Параметр одноразовый: отработал — стираем из
 * адреса, иначе «Снять выбор» отменялся бы следующим же рендером, а ссылка из
 * истории браузера заново отмечала бы весь список.
 *
 * Стираем его ПОСЛЕ первой загрузки и тогда, когда отмечать было нечего
 * (правка по ревью 2026-09-20): пустой отбор оставлял `select=all` висеть в
 * адресе, и ссылка на «пустые остатки» жила дальше как ссылка «отметь всё».
 * Поэтому хук живёт в панели вкладки, а не в списке строк: при нуле строк
 * список не отрисован, и чистить адрес было бы некому.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { isPlaceable } from "./stock-row-actions";
import type { StockLevelDto } from "../../../lib/api-wms";

/** Параметр входа с дашборда: `?select=all`. */
export const SELECT_PARAM = "select";

interface Options {
  /** Строки текущего отбора (уже отфильтрованные). */
  rows: StockLevelDto[];
  /** Галочки включены — активен чипс «Без места». */
  enabled: boolean;
  /** Данные загружены: до этого предвыбирать нечего. */
  ready: boolean;
  /**
   * phase19.22 — какую строку можно отметить. По умолчанию — «можно
   * разместить»; в отборах «Без этикеток» / «Устарели» — ещё и «можно
   * напечатать» (есть партия). Правило задаёт панель, хук его не знает.
   */
  canSelect?: (level: StockLevelDto) => boolean;
}

export interface StockSelectionApi {
  /** Выбранные строки в порядке отбора. */
  selected: StockLevelDto[];
  /** Быстрая проверка для чекбокса строки. */
  isSelected: (level: StockLevelDto) => boolean;
  toggle: (level: StockLevelDto) => void;
  /** Отметить все партии отбора, которые вообще можно разместить. */
  selectAll: () => void;
  clear: () => void;
}

export function useStockSelection({
  rows,
  enabled,
  ready,
  canSelect = isPlaceable,
}: Options): StockSelectionApi {
  const [searchParams, setSearchParams] = useSearchParams();
  const [ids, setIds] = useState<ReadonlySet<string>>(() => new Set());
  // Предвыбор с дашборда срабатывает один раз за жизнь экрана: повторный
  // прогон эффекта (перезагрузка списка) не должен возвращать снятые галочки.
  const presetDone = useRef(false);

  const placeable = useMemo(() => rows.filter(canSelect), [rows, canSelect]);

  const selectAll = useCallback(() => {
    setIds(new Set(placeable.map((level) => level.id)));
  }, [placeable]);

  const clear = useCallback(() => setIds(new Set()), []);

  // Отбор выключили — выбор теряет смысл: панель снизу исчезает вместе с ним.
  useEffect(() => {
    if (!enabled) {
      setIds((prev) => (prev.size === 0 ? prev : new Set()));
    }
  }, [enabled]);

  useEffect(() => {
    if (presetDone.current) return;
    if (searchParams.get(SELECT_PARAM) !== "all") return;
    // Ждём именно загрузку, а не строки: при пустом отборе отмечать нечего,
    // но параметр всё равно обязан уйти из адреса.
    if (!ready) return;
    presetDone.current = true;
    if (enabled) selectAll();
    const next = new URLSearchParams(searchParams);
    next.delete(SELECT_PARAM);
    // replace: ссылка с дашборда не должна копиться в истории вместе с
    // «Назад», которое возвращало бы предвыбор.
    setSearchParams(next, { replace: true });
  }, [enabled, ready, searchParams, selectAll, setSearchParams]);

  const toggle = useCallback((level: StockLevelDto) => {
    setIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(level.id)) next.add(level.id);
      return next;
    });
  }, []);

  const selected = useMemo(
    () => (enabled ? placeable.filter((level) => ids.has(level.id)) : []),
    [enabled, placeable, ids],
  );

  const isSelected = useCallback(
    (level: StockLevelDto) => ids.has(level.id),
    [ids],
  );

  return { selected, isSelected, toggle, selectAll, clear };
}
